import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { api, type JsonObject } from "../api/client";
import { i18n } from "../i18n";
import { readable } from "../view-model";

interface RetrievalStatus {
  backend: string;
  mode: string;
}

@customElement("rag-view")
export class RagView extends LitElement {
  @state() private results: JsonObject[] = [];
  @state() private retrievalStatus: RetrievalStatus | null = null;
  @state() private error = "";
  @state() private hasSearched = false;
  @state() private busy = false;

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("languagechange", this.languageChanged);
  }

  disconnectedCallback() {
    window.removeEventListener("languagechange", this.languageChanged);
    super.disconnectedCallback();
  }

  private languageChanged = () => this.requestUpdate();

  private async submit(event: SubmitEvent) {
    event.preventDefault();
    this.busy = true;
    this.error = "";
    const values = new FormData(event.currentTarget as HTMLFormElement);

    try {
      const data = await api.rag(
        String(values.get("q")),
        String(values.get("top_k") || 5),
      );
      this.results = data.items || [];
      this.retrievalStatus = {
        backend: data.backend || "lancedb",
        mode: data.mode || "vector",
      };
      this.hasSearched = true;
    } catch (error) {
      this.error = (error as Error).message;
      this.results = [];
      this.retrievalStatus = null;
      this.hasSearched = true;
    } finally {
      this.busy = false;
    }
  }

  private statusText(): string {
    if (this.error) {
      return i18n.t("ragError", { message: this.error });
    }
    if (!this.retrievalStatus) return i18n.t("ragInitial");
    return i18n.t("ragLanceStatus");
  }

  private resultText(item: JsonObject): string {
    const text = item.text || item.search_text || item.content || item.summary;
    return text ? String(text) : JSON.stringify(item, null, 2);
  }

  render() {
    return html`
      <page-heading
        heading=${i18n.t("ragTitle")}
        description=${i18n.t("ragDescription")}
      ></page-heading>
      <surface-window heading=${i18n.t("ragRequest")}>
        <form class="toolbar" @submit=${this.submit}>
          <a2k-text-field
            label=${i18n.t("ragQuestion")}
            name="q"
            placeholder=${i18n.t("ragQuestionPlaceholder")}
            required
          ></a2k-text-field>
          <label>
            ${i18n.t("ragTopK")}
            <input
              class="a2k-native-input"
              name="top_k"
              type="number"
              value="5"
              min="1"
              max="50"
            />
          </label>
          <a2k-button type="submit" ?disabled=${this.busy}>
            ${this.busy ? i18n.t("working") : i18n.t("ragSearch")}
          </a2k-button>
        </form>
      </surface-window>
      ${
        this.busy
          ? html`<a2k-progress></a2k-progress>`
          : html`
              <surface-window heading=${i18n.t("ragResults")}>
                <p class="status ${this.error ? "status-error" : ""}">
                  ${this.statusText()}
                </p>
                ${
                  this.hasSearched && !this.error && this.results.length === 0
                    ? html`<p class="empty-state">${i18n.t("ragEmpty")}</p>`
                    : html`
                        <a2k-stack>
                          ${this.results.map(
                            (item, index) => html`
                              <a2k-panel class="result-item">
                                <strong>
                                  #${index + 1} ·
                                  ${readable(
                                    item.title ||
                                      item.id ||
                                      i18n.t("ragResultFallback", {
                                        index: index + 1,
                                      }),
                                  )}
                                </strong>
                                <p>${this.resultText(item)}</p>
                              </a2k-panel>
                            `,
                          )}
                        </a2k-stack>
                      `
                }
              </surface-window>
            `
      }
    `;
  }
}
